import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout1 from "../../Layouts/Layout1";
import LayoutContainer from "../../Layouts/LayoutContainer";
import { ChevronLeft, HelpCircle } from "lucide-react";
import RentalDataUseModal from "./RentalDataUseModal";
import { useRentalProfile } from "../../context/RentalProfileContext";
import { RENTAL_PROFILE_SECTIONS } from "../../Constants/rentalProfile";

const COUNTS = [0, 1, 2, 3, 4];

const CountSegment = ({ value, onChange }) => (
  <div className="flex shrink-0 rounded-full bg-[#F3F4F6] p-1 shadow-sm">
    {COUNTS.map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className={`min-w-[2.5rem] rounded-full px-2 py-2 text-sm font-semibold transition-all sm:min-w-[2.75rem] ${
          value === n
            ? "bg-[#000929] text-white shadow-md"
            : "text-[#000929]"
        }`}
      >
        {n}
      </button>
    ))}
  </div>
);

const PetRow = ({ label, value, onChange }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <span className="text-sm font-semibold text-[#000929]">{label}</span>
    <CountSegment value={value} onChange={onChange} />
  </div>
);

const HouseholdPets = () => {
  const navigate = useNavigate();
  const { profile, loading, saving, updateSection } = useRentalProfile();
  const [dataUseModalOpen, setDataUseModalOpen] = useState(false);
  const [dogs, setDogs] = useState(0);
  const [cats, setCats] = useState(0);
  const [other, setOther] = useState(0);

  useEffect(() => {
    if (loading) return;
    const pets = profile.householdPets;
    setDogs(pets.dogs ?? 0);
    setCats(pets.cats ?? 0);
    setOther(pets.other ?? 0);
  }, [loading, profile.householdPets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await updateSection(RENTAL_PROFILE_SECTIONS.HOUSEHOLD_PETS, {
      dogs,
      cats,
      other,
    });
    if (ok) navigate("/rental-applications");
  };

  return (
    <Layout1>
      <LayoutContainer className="max-w-xl py-8 md:py-12 lg:py-14">
        <button
          type="button"
          onClick={() => navigate("/rental-applications")}
          className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-[#097FE6] hover:underline"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2} />
          Back
        </button>

        <h1 className="text-2xl font-bold tracking-tight text-[#000929] md:text-3xl">
          Pets
        </h1>

        <div className="mt-8 rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm md:p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            <p className="text-sm leading-relaxed text-[#6B7280]">
              Let the property manager know if you have pets.
            </p>

            <div className="space-y-6">
              <PetRow label="Dogs" value={dogs} onChange={setDogs} />
              <PetRow label="Cats" value={cats} onChange={setCats} />
              <PetRow label="Other pets" value={other} onChange={setOther} />
            </div>

            <div className="border-t border-[#E5E7EB] pt-8">
              <button
                type="submit"
                disabled={loading || saving}
                className="w-full rounded-lg bg-[#097FE6] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#0868c4] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save and back"}
              </button>
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => navigate("/rental-applications")}
                  className="text-sm font-semibold text-[#000929] hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>

        <button
          type="button"
          onClick={() => setDataUseModalOpen(true)}
          className="mt-10 inline-flex items-center gap-2 text-sm text-[#6B7280] underline decoration-[#6B7280] underline-offset-2 hover:text-[#1A1A1A]"
        >
          <HelpCircle className="h-4 w-4 shrink-0" strokeWidth={2} />
          How this information is used
        </button>
      </LayoutContainer>

      <RentalDataUseModal
        variant="personalDetails"
        open={dataUseModalOpen}
        onClose={() => setDataUseModalOpen(false)}
      />
    </Layout1>
  );
};

export default HouseholdPets;
